"use client";

import * as React from "react";

// @components
import Link from "next/link";

// @hooks
import { useVariation } from "../store";
import { useTableData } from "@/hooks/use-liquidity";

const TABLE_HEAD = [
  "Chain",
  "DEX",
  "Pair",
  "Liquidity",
  "Ask",
  "Bid",
  "Live Price",
];

function TH({
  children,
  isLast,
}: {
  children: React.ReactNode;
  isLast?: boolean;
}) {
  return (
    <th
      className={`${
        isLast ? "" : "border-r"
      } border-b border-secondary bg-secondary/50 p-4 text-sm font-medium text-foreground`}
    >
      {children}
    </th>
  );
}

function TD({
  children,
  isFirst,
  isLast,
  isLastRow,
  noPadding,
}: {
  children: React.ReactNode;
  isFirst?: boolean;
  isLast?: boolean;
  isLastRow?: boolean;
  noPadding?: boolean;
}) {
  return (
    <td
      className={`${isLast ? "" : "border-r border-secondary"} ${
        isFirst
          ? "bg-secondary/50 font-medium text-foreground"
          : "font-normal text-white"
      } ${isLastRow ? "" : "border-b border-secondary"} ${
        noPadding ? "p-2" : "p-4"
      } w-max min-w-max text-center text-sm font-medium`}
    >
      {children}
    </td>
  );
}

export function Table() {
  const { data } = useTableData();
  const variation = useVariation((state: any) => state.variation);

  const filteredData = React.useMemo(() => {
    if (variation === "EHEX & PHEX") {
      return data;
    }

    return data.filter((el: any) => {
      const pairExists = el.pairs.find(
        (item: string) => item.toUpperCase() === variation,
      );

      if (pairExists) {
        return el;
      }
    });
  }, [data, variation]);

  return (
    <table className="w-full table-auto border-collapse border-spacing-0 overflow-hidden">
      <thead>
        <tr className="capitalize">
          {TABLE_HEAD?.map((head, idx) => (
            <TH key={idx} isLast={idx === TABLE_HEAD.length - 1}>
              {head}
            </TH>
          ))}
        </tr>
      </thead>
      <tbody>
        {filteredData?.map(
          (
            {
              chain,
              dex,
              pairs,
              pool,
              liquidity,
              ask,
              bid,
              askUSD,
              bidUSD,
              livePricePair1,
              livePricePair2,
            }: any,
            idx: number,
          ) => {
            const isLastRow = filteredData.length - 1 === idx;

            return (
              <tr key={`${pairs[0]}/${pairs[1]}-${liquidity}`}>
                <TD isLastRow={isLastRow} isFirst>
                  {chain}
                </TD>
                <TD isLastRow={isLastRow}>{dex}</TD>
                <TD isLastRow={isLastRow}>
                  <Link href={pool} target="_blank">
                    {pairs[0]}/{pairs[1]}
                  </Link>
                </TD>
                <TD isLastRow={isLastRow}>{liquidity}</TD>
                <TD isLastRow={isLastRow}>
                  <div className="flex flex-col gap-2">
                    <span>{askUSD}</span>
                    <span className="text-xs text-foreground">{ask}</span>
                  </div>
                </TD>
                <TD isLastRow={isLastRow}>
                  <div className="flex flex-col gap-2">
                    <span>{bidUSD}</span>
                    <span className="text-xs text-foreground">{bid}</span>
                  </div>
                </TD>
                <TD isLastRow={isLastRow} isLast noPadding>
                  <div className="flex h-full w-full gap-2">
                    <div className="flex w-full flex-col gap-2 rounded-md border border-secondary bg-secondary/50 p-2">
                      <span>1 {pairs[0]}</span>
                      <span className="text-xs text-foreground">
                        {livePricePair1}
                      </span>
                    </div>
                    <div className="flex w-full flex-col gap-2 rounded-md border border-secondary bg-secondary/50 p-2">
                      <span>1 {pairs[1]}</span>
                      <span className="text-xs text-foreground">
                        {livePricePair2}
                      </span>
                    </div>
                  </div>
                </TD>
              </tr>
            );
          },
        )}
      </tbody>
    </table>
  );
}
